import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { BooleanParam, useQueryParams, withDefault } from "use-query-params";
import { CommandsDirectory } from "@components/commands/lib";
import { useCommands } from "@components/commands/useCommands";
import { RelayModal } from "@components/commands/RelayModal";
import { GustoConnectionModal } from "@components/gusto/GustoConnectionModal";
import { useEffectOnce } from "@hooks/useEffectOnce";
import { useEntities } from "@hooks/useEntities";
import { useEntitiesContexts } from "@hooks/useEntitiesContexts";
import {
    useWorkspaceUserCache,
    WORKSPACE_USER_CACHE_KEYS,
} from "@hooks/useWorkspaceUserCache";
import {
    AccountsView,
    AccountsViewSchema,
} from "@common/types/views/accountsView";
import { ExpandedState } from "@tanstack/react-table";
import { Loader } from "@design-system/Loader/Loader";
import { useCurrentWorkspaceMembers } from "@api/workspaceMember.api";
import { StandardModal } from "@design-system/Modal/Modal";
import { ViewTable, ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { TransactionEnrichmentPausedBanner } from "@components/accountingMigration/TransactionEnrichmentPausedBanner";
import { useStripeExpressReturn } from "@hooks/useStripeExpressReturn";
import { AccountsHeader } from "./AccountsHeader";
import { AccountsSegmentBar } from "./AccountsSegmentBar/AccountsSegmentBar";
import { accountsView, accountsViewConfig } from "./views/accountsViewConfig";
import { buildAccountsTableData } from "./views/accountsViewTableData";
import styles from "./AccountsPage.module.scss";
import viewStyles from "./views/accountsView.module.scss";

export const AccountsPage: React.FC = () => {
    const [queryParams] = useQueryParams({
        openRelayModal: withDefault(BooleanParam, false),
        openGustoModal: withDefault(BooleanParam, false),
        openAddAccountsModal: withDefault(BooleanParam, false),
    });

    const { open } = useCommands();

    useStripeExpressReturn();

    const [openRelayModal, setOpenRelayModal] = useState(
        queryParams.openRelayModal,
    );
    const [openGustoModal, setOpenGustoModal] = useState(
        queryParams.openGustoModal,
    );
    const [selectedEntityId, setSelectedEntityId] = useState<number | null>(
        null,
    );

    useEffectOnce(() => {
        if (queryParams.openAddAccountsModal) {
            open(CommandsDirectory.ACCOUNTS);
        }
    });

    const [expanded, setExpanded] = useState<ExpandedState>({});
    const tableRef = useRef<ViewTableHandle>(null);
    const hasInitializedExpandedRef = useRef(false);

    const [cachedView, setCachedView] = useWorkspaceUserCache<AccountsView>(
        WORKSPACE_USER_CACHE_KEYS.ACCOUNTS_PAGE_VIEW_CACHE_KEY,
        AccountsViewSchema,
    );

    const [view, setView] = useState<AccountsView>(cachedView ?? accountsView);

    const handleViewChange = useCallback(
        (newView: AccountsView) => {
            setView(newView);
            setCachedView(newView);
        },
        [setCachedView],
    );

    const entitiesContext = useEntitiesContexts();
    const entitiesWithBalance = useEntities({
        onlyWithBalance: true,
    });

    const { data: workspaceMembers, isFetched: membersFetched } =
        useCurrentWorkspaceMembers();

    const tableData = useMemo(
        () =>
            buildAccountsTableData({
                entitiesContext:
                    entitiesContext?.filter(
                        ({ entity }) =>
                            selectedEntityId === null ||
                            entity.id === selectedEntityId,
                    ) ?? undefined,
                workspaceMembers: workspaceMembers ?? undefined,
            }),
        [entitiesContext, workspaceMembers, selectedEntityId],
    );

    useEffect(() => {
        if (
            hasInitializedExpandedRef.current ||
            !tableData ||
            tableData.length === 0
        ) {
            return;
        }
        hasInitializedExpandedRef.current = true;
        const expandedState: Record<string, boolean> = {};
        tableData.forEach((row) => {
            if (row.accounts.length > 0) {
                expandedState[row.id] = true;
            }
        });
        setExpanded(expandedState);
    }, [tableData]);

    if (!membersFetched) {
        return <Loader />;
    }

    return (
        <>
            <TransactionEnrichmentPausedBanner />
            <div>
                <AccountsHeader
                    view={view}
                    onViewChange={handleViewChange}
                    expanded={expanded}
                    tableRef={tableRef}
                />
                <AccountsSegmentBar
                    entities={entitiesWithBalance}
                    selectedEntityId={selectedEntityId}
                    onEntityClick={(entityId) => {
                        setSelectedEntityId((currentEntityId) =>
                            currentEntityId === entityId ? null : entityId,
                        );
                    }}
                />
            </div>

            <main className={styles.table}>
                <ViewTable
                    ref={tableRef}
                    data={tableData}
                    view={view}
                    config={accountsViewConfig}
                    className={viewStyles.accountsView}
                    nestingIndentPx={24}
                    fixedTopOffsetPx
                    rowBorders
                    enableColumnResizing
                    expanded={expanded}
                    onExpandedChange={setExpanded}
                    onViewChange={(newView) =>
                        handleViewChange(newView as AccountsView)
                    }
                />
            </main>

            <StandardModal
                show={openRelayModal}
                onHide={() => setOpenRelayModal(false)}
            >
                <RelayModal defaultEntity={undefined} />
            </StandardModal>

            <GustoConnectionModal
                show={openGustoModal}
                onHide={() => setOpenGustoModal(false)}
            />
        </>
    );
};
